import { useState } from 'react';
import { manualUrl } from '../api/client';
import Logo from '../components/Logo';
import PdfViewer from '../components/PdfViewer';
import { SUPPORT_EMAIL, supportMailto } from '../support';

function formatRange(training) {
  return `${training.startDate ?? '?'} — ${training.endDate ?? '?'}`;
}

// Why there's no manual to open. The API only issues links for a course the
// learner is marked Competent on, so say which of those two it is.
function manualNotice(training) {
  if (training.competent) return 'Manual not available yet';
  if (training.status === 'In Training') return 'Available once you are marked competent';
  return 'Not marked competent';
}

/**
 * One button when there's a single manual; a language dropdown beside it when
 * the course has translations. Each card keeps its own selection, so this has to
 * be a component rather than inline markup.
 */
function ManualLinks({ manuals, courseName, onOpen }) {
  const [lang, setLang] = useState(manuals[0].lang);
  const chosen = manuals.find((m) => m.lang === lang) ?? manuals[0];

  // A button, not a link: the manual opens in our own canvas viewer rather than
  // the browser's PDF viewer, which would hand the learner Download and Print.
  const button = (
    <button
      type="button"
      className="manual-button"
      lang={chosen.lang}
      onClick={() => onOpen({ url: manualUrl(chosen.token), title: courseName })}
    >
      View manual
    </button>
  );

  if (manuals.length === 1) return button;

  return (
    <span className="manual-links">
      <select
        className="lang-select"
        value={lang}
        onChange={(e) => setLang(e.target.value)}
        aria-label="Manual language"
      >
        {manuals.map((m) => (
          <option key={m.lang} value={m.lang}>
            {m.label}
          </option>
        ))}
      </select>
      {button}
    </span>
  );
}

export default function ResultsScreen({ trainings, onBack }) {
  const firstName = trainings[0]?.firstName || 'there';
  const [viewing, setViewing] = useState(null);

  if (viewing) {
    return <PdfViewer url={viewing.url} title={viewing.title} onClose={() => setViewing(null)} />;
  }

  return (
    <div className="results-screen">
      <div className="logo-row">
        <Logo size="small" />
      </div>
      <header className="results-header">
        <div className="accent-bar" />
        <div className="step-label">Your trainings</div>
        <h1 className="title">Hi {firstName}</h1>
        <p className="subtitle">Open the manual for a course you passed</p>
      </header>

      <ul className="course-list">
        {trainings.map((training) => {
          const manuals = training.manuals ?? [];
          return (
            <li key={training.enrollId} className="course-card">
              <div className="card-accent" />
              <div className="card-body">
                <div className="course-name">{training.courseName ?? 'Course'}</div>
                {training.designation && <div className="designation">{training.designation}</div>}
                <div className="card-footer">
                  <span className="dates">
                    {formatRange(training)}
                    {training.status && ` · ${training.status}`}
                  </span>
                  {manuals.length > 0 ? (
                    <ManualLinks
                      manuals={manuals}
                      courseName={training.courseName ?? 'Manual'}
                      onOpen={setViewing}
                    />
                  ) : (
                    <span className="manual-missing">{manualNotice(training)}</span>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Always shown, not just when something is missing — a learner who can't
          find what they came for shouldn't have to work out who to ask. */}
      <div className="notice notice-standing" role="status">
        <p className="notice-help">
          Having trouble seeing everything? Email{' '}
          <a href={supportMailto('Learner manuals — having trouble')}>{SUPPORT_EMAIL}</a>{' '}
          and we'll help you.
        </p>
      </div>

      <button className="primary-button back-button" onClick={onBack}>
        Done — back to search
      </button>
    </div>
  );
}
